import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ApiError, billing as billingApi } from "@/lib/api";

/** Shape the server sends with a 402 (see server/plan_limits.py). */
interface PlanLimitBody {
  code?: string;
  error?: string;
  remedies?: string[];
  upgrade_url?: string;
}

/**
 * Does `err` represent "you need a paid plan for this"?
 *
 * Keyed on the 402 rather than the message: 402 means unpaid, 403 means
 * forbidden, and conflating them would offer an upgrade to someone whose
 * problem is permissions.
 */
export function isPlanLimit(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 402;
}

function planLimitBody(err: ApiError): PlanLimitBody {
  return (err.body ?? {}) as PlanLimitBody;
}

/**
 * Inline upgrade prompt shown where a plan limit was hit, rather than a
 * redirect to a pricing page.
 *
 * Two deliberate choices:
 *
 *  - It leads with the *free* way forward when the server offered one.
 *    The private-document limit can always be sidestepped by publishing, and
 *    a prompt that only says "pay" will lose people who would happily have
 *    made the document public.
 *  - It goes straight to Stripe Checkout instead of to /billing. The user is
 *    mid-task; sending them to read a pricing page loses the thread.
 */
export function UpgradePrompt({
  error,
  onMakePublic,
  publicLabel = "Make it public instead",
}: {
  error: ApiError;
  onMakePublic?: () => void;
  publicLabel?: string;
}) {
  const body = planLimitBody(error);
  const checkoutMut = useMutation({
    mutationFn: () => billingApi.createCheckout("monthly"),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
  });

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-4 text-sm">
      <div className="mb-3">
        {body.error ?? "This needs a paid plan."}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {onMakePublic && (
          <button
            type="button"
            onClick={onMakePublic}
            className="rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            {publicLabel}
          </button>
        )}
        <button
          type="button"
          onClick={() => checkoutMut.mutate()}
          disabled={checkoutMut.isPending}
          className="rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-3 py-1.5 text-sm font-medium disabled:opacity-50"
        >
          {checkoutMut.isPending ? "Opening checkout…" : "Upgrade"}
        </button>
        <Link to="/billing" className="text-xs text-zinc-500 hover:underline">
          See plans
        </Link>
      </div>
      {checkoutMut.error && (
        <div className="text-xs text-red-600 dark:text-red-400 mt-2">
          Couldn&rsquo;t open checkout. <Link to="/billing" className="underline">Try from the billing page</Link>.
        </div>
      )}
    </div>
  );
}
